import { PlusOutlined } from '@ant-design/icons';
import { getLeadAttributes, type GetLeadAttributesType } from '@repo/advanced-filters';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Col, Divider, Flex, Row, Typography,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type LeadUpdateNodeType, nodeSchemas } from 'automations/types/nodeTypes';
import { saveTriggerNode } from 'automations/utils/triggerNode';
import React, { useCallback, useEffect, useState } from 'react';
import { ZodError } from 'zod';

import AutomationsCriteriaConditionsWrapper from '../AutomationsCriteriaConditionsWrapper';
import { type BuilderUiProps } from '../AutomationsCriteriaConditionsWrapper/AutomationsCriteriaConditionsWrapper';
import ComparatorRenderer from '../ComparatorRenderer';
import ShowConditionsUi from '../ShowConditionsUi';
import ChangedAttributes from './ChangedAttributes';
import styles from './LeadUpdateMenu.module.scss';

type Criteria = LeadUpdateNodeType['data']['additionalOptions']['updateLead']['criterias'][number];

export default function LeadUpdateMenu({
  node,
}: {
  node: NodeProps<LeadUpdateNodeType>;
}) {
  const automationId = useAutomationId();
  const dispatch = useAutomationDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const {
    data: leadAttributesData,
  } = useGetGraphqlDataQuery<UseQueryResult<GetLeadAttributesType>>({
    document: getLeadAttributes,
  });

  const [
    updateLeadFields, setUpdateLeadFields,
  ] = useState<Criteria[]>(
    node.data.additionalOptions?.updateLead?.criterias ?? [],
  );

  const [additionalConditionsId, setAdditionalConditionsId] = useState<number | undefined>(
    node.data.additionalOptions?.criteriaGroupId ?? undefined,
  );

  const [removalConditionsId, setRemovalConditionsId] = useState<number | undefined>(
    node.data.additionalOptions?.removableCriteriaGroupId ?? undefined,
  );

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      // this will throw an error if the parsing fails
      const parsedNodeData = nodeSchemas.LeadUpdateNodeData.parse({
        ...node.data,
        additionalOptions: {
          updateLead: {
            criterias: updateLeadFields,
            comparator: 'or',
          },
          criteriaGroupId: additionalConditionsId,
          removableCriteriaGroupId: removalConditionsId,
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));

      await dispatch(saveTriggerNode(node.id, automationId, contentMode));
      message.success(translate(`#{${TranslationEntity.automation}} updated`, [{ capitalize: true }]));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [additionalConditionsId, automationId, dispatch, node.data, node.id, setMenuOpen,
    removalConditionsId, updateLeadFields, contentMode]);

  const updateExistingFields = useCallback((
    field: Criteria,
    index: number,
  ) => {
    setUpdateLeadFields((prev) => {
      const newFields = [...prev];
      newFields[index] = field;
      return newFields;
    });
  }, [setUpdateLeadFields]);

  const deleteFieldAtIndex = useCallback((index: number) => {
    setUpdateLeadFields((prev) => prev.filter((_, i) => i !== index));
  }, [setUpdateLeadFields]);

  const addNewDefaultField = useCallback(() => {
    setUpdateLeadFields((prev) => [
      ...prev,
      { schemaName: leadAttributesData?.leadAttributes[0]?.schemaName ?? '' },
    ]);
  }, [setUpdateLeadFields, leadAttributesData]);

  useEffect(() => {
    if (leadAttributesData?.leadAttributes.length && updateLeadFields.length === 0) {
      addNewDefaultField();
    }
  }, [leadAttributesData?.leadAttributes.length, addNewDefaultField, updateLeadFields.length]);

  const ConditionsBuilderUi = useCallback((props: BuilderUiProps) => (
    <ShowConditionsUi
      {...props}
      title="Set Conditions"
      btnClassName={styles.addBtn}
    />
  ), []);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(
          `#{${TranslationEntity.automation}} Trigger - When #{${TranslationEntity.lead}} is added`,
          [{ capitalize: true }, { capitalize: true, withArticle: true }],
        ),
      }}
    >
      <Card title={contentMode === 'edit' ? 'Set Trigger' : 'Edit Trigger'} className={styles.containerCard}>
        <div>
          <Flex gap={8} vertical>
            {updateLeadFields.map((field, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>
                <ChangedAttributes
                  field={field}
                  currentIndex={index}
                  updateExistingFields={updateExistingFields}
                  deleteFieldAtIndex={deleteFieldAtIndex}
                />
                { index !== updateLeadFields.length - 1 && (
                  <Row>
                    <Col span={18}>
                      <ComparatorRenderer
                        comparator={node.data.additionalOptions?.updateLead?.comparator ?? 'or'}
                        className={styles.comparator}
                      />
                    </Col>
                  </Row>
                )}
              </React.Fragment>
            ))}
          </Flex>
          <Row>
            <Col span={6} />
            <Col span={18}>
              <Button
                className={styles.addBtn}
                onClick={addNewDefaultField}
                icon={<PlusOutlined />}
                type="dashed"
              >
                Add Lead Field
              </Button>
            </Col>
          </Row>
        </div>

        <Divider dashed />
        <div>
          <Row align="middle">
            <Col span={6}>
              <Typography.Text strong>Set Additional Conditions</Typography.Text>
            </Col>
            <Col span={18}>
              <AutomationsCriteriaConditionsWrapper
                criteriaId={additionalConditionsId}
                onCriteriaChange={(value) => setAdditionalConditionsId(value)}
                builderUi={ConditionsBuilderUi}
              />
            </Col>
          </Row>
        </div>
        <Divider dashed />
        <div>
          <Row align="middle">
            <Col span={6}>
              <Typography.Text strong>Remove Lead(s) from this automation</Typography.Text>
            </Col>
            <Col span={18}>
              <AutomationsCriteriaConditionsWrapper
                criteriaId={removalConditionsId}
                onCriteriaChange={(value) => setRemovalConditionsId(value)}
                builderUi={ConditionsBuilderUi}
              />
            </Col>
          </Row>
        </div>
      </Card>

      <Flex justify="flex-end" gap={16}>
        <Button onClick={() => setMenuOpen(false)}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          Set Trigger
        </Button>
      </Flex>
    </NodeMenu>
  );
}
